import React, { useState } from 'react';

const problems = [
  {
    id: 1,
    title: "Two Sum",
    difficulty: "Easy",
    description: "Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target. You may assume that each input would have exactly one solution, and you may not use the same element twice.",
    examples: [
      { input: "nums = [2,7,11,15], target = 9", output: "[0,1]", explanation: "Because nums[0] + nums[1] == 9, we return [0, 1]." },
      { input: "nums = [3,2,4], target = 6", output: "[1,2]", explanation: "nums[1] + nums[2] == 6" }
    ],
    hints: {
      r: "What are the inputs? What should the output be? Can you use the same element twice? Is there always a solution?",
      e: "Try edge cases: What if the array has only 2 elements? What about negative numbers? What if target is 0?",
      a: "Consider: brute force O(n²) vs hash map O(n). What are the tradeoffs?"
    }
  },
  {
    id: 2,
    title: "Valid Parentheses",
    difficulty: "Easy",
    description: "Given a string s containing just the characters '(', ')', '{', '}', '[' and ']', determine if the input string is valid. An input string is valid if: Open brackets must be closed by the same type of brackets, and open brackets must be closed in the correct order.",
    examples: [
      { input: 's = "()"', output: "true", explanation: "Simple matching pair" },
      { input: 's = "()[]{}"', output: "true", explanation: "Multiple valid pairs" },
      { input: 's = "(]"', output: "false", explanation: "Mismatched brackets" }
    ],
    hints: {
      r: "What characters are valid? What makes a string 'valid'? What about empty strings?",
      e: "Consider: nested brackets like '({[]})'. What about '([)]'? Empty string?",
      a: "Think about what data structure helps match opening with closing brackets in order."
    }
  },
  {
    id: 3,
    title: "Reverse Linked List",
    difficulty: "Easy",
    description: "Given the head of a singly linked list, reverse the list, and return the reversed list.",
    examples: [
      { input: "head = [1,2,3,4,5]", output: "[5,4,3,2,1]", explanation: "The list is reversed" },
      { input: "head = [1,2]", output: "[2,1]", explanation: "Two node case" },
      { input: "head = []", output: "[]", explanation: "Empty list edge case" }
    ],
    hints: {
      r: "What is a linked list? What does 'reverse' mean here? Should you create new nodes or modify pointers?",
      e: "Edge cases: empty list, single node, two nodes. Draw it out!",
      a: "Iterative vs recursive? What pointers do you need to track?"
    }
  }
];

const steps = [
  { key: 'R', name: 'Repeat', description: 'Restate the problem in your own words. Ask clarifying questions.', type: 'text' },
  { key: 'E', name: 'Examples', description: 'Create example inputs and expected outputs. Include edge cases.', type: 'text' },
  { key: 'A', name: 'Approach', description: 'Plan your approach before coding. Discuss time/space complexity.', type: 'text' },
  { key: 'C', name: 'Code', description: 'Implement your solution. Start with the structure, then fill in details.', type: 'code' },
  { key: 'T', name: 'Test', description: 'Walk through your code with test cases. Trace the execution step by step.', type: 'code' },
  { key: 'O', name: 'Optimize', description: 'Analyze Big-O complexity. Can you improve time or space efficiency?', type: 'code' }
];

export default function REACTOApp() {
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({
    R: '', E: '', A: '', C: '', T: '', O: ''
  });
  const [showHint, setShowHint] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [aiFeedback, setAiFeedback] = useState('');
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Short descriptions for hover tooltips
  const stepTooltips = {
    R: "Restate the problem to ensure understanding",
    E: "Create examples including edge cases",
    A: "Plan your approach before coding",
    C: "Write clean, readable code",
    T: "Trace through with test cases",
    O: "Analyze and improve complexity"
  };

  // Color palette based on the light blue scheme
  const themes = {
    dark: {
      bg: 'linear-gradient(135deg, #0a1a1f 0%, #0f2027 50%, #1a3a4a 100%)',
      cardBg: 'rgba(134, 197, 216, 0.05)',
      cardBorder: 'rgba(134, 197, 216, 0.15)',
      cardHover: 'rgba(134, 197, 216, 0.1)',
      text: '#e8f4f8',
      textMuted: '#8bb8c9',
      textDim: '#5a8a9a',
      primary: '#86C5D8',
      primaryLight: '#ADD8E6',
      primaryLighter: '#CAE9F5',
      primaryGlow: 'rgba(134, 197, 216, 0.4)',
      accent: '#AFDCEB',
      success: '#5dd4a8',
      successBg: 'rgba(93, 212, 168, 0.15)',
      pink: '#f0a5b8',
      pinkBg: 'rgba(240, 165, 184, 0.15)',
      warning: '#f0c674',
      warningBg: 'rgba(240, 198, 116, 0.1)',
      explanation: '#86C5D8',
      explanationBg: 'rgba(134, 197, 216, 0.1)',
      inputBg: 'rgba(0, 20, 30, 0.5)',
      navBg: 'rgba(10, 26, 31, 0.8)',
      panelBg: 'rgba(0, 15, 20, 0.3)',
      codeBg: 'rgba(0, 20, 30, 0.6)',
    },
    light: {
      bg: 'linear-gradient(135deg, #F0F8FF 0%, #E8F4F8 50%, #CAE9F5 100%)',
      cardBg: 'rgba(255, 255, 255, 0.7)',
      cardBorder: 'rgba(134, 197, 216, 0.3)',
      cardHover: 'rgba(255, 255, 255, 0.9)',
      text: '#1a3a4a',
      textMuted: '#4a7a8a',
      textDim: '#6a9aaa',
      primary: '#5a9fb5',
      primaryLight: '#86C5D8',
      primaryLighter: '#ADD8E6',
      primaryGlow: 'rgba(90, 159, 181, 0.3)',
      accent: '#86C5D8',
      success: '#3aaa7a',
      successBg: 'rgba(58, 170, 122, 0.15)',
      pink: '#d85a78',
      pinkBg: 'rgba(216, 90, 120, 0.12)',
      warning: '#c9a030',
      warningBg: 'rgba(201, 160, 48, 0.12)',
      explanation: '#5a9fb5',
      explanationBg: 'rgba(134, 197, 216, 0.2)',
      inputBg: 'rgba(255, 255, 255, 0.8)',
      navBg: 'rgba(255, 255, 255, 0.85)',
      panelBg: 'rgba(255, 255, 255, 0.4)',
      codeBg: 'rgba(240, 248, 255, 0.9)',
    }
  };

  const theme = isDarkMode ? themes.dark : themes.light;

  const stepExplanations = {
    R: "Although it might feel redundant, restating the problem in your own words allows the interviewer to understand your interpretation of the task. It also allows a safe space to ask clarifying questions, clear any misunderstandings, and avoid assumptions. Remember - interviews are a conversation, and a dialogue.",
    E: "Use examples to demonstrate your thinking and use this step to discover edge cases before you write your code.",
    A: "Before writing code, pause and vocalize your thought process. Include the interviewer in the dialogue, and your problem solving process. Consider trade offs between a BFS and an optimized solution!",
    C: "Now that you've planned, coding becomes much smoother. Interviewers want to see clean, readable code — not perfect code. Talk through what you're writing, and don't be afraid to use helper functions or pseudocode first. Communication during coding shows collaboration skills.",
    T: "Walking through your code with test cases proves it actually works and shows attention to detail. Trace through line by line with a specific input — this often catches bugs before the interviewer does. It demonstrates the debugging skills you'd use on the job.",
    O: "Save time to discuss space-time complexity. Even if you're \"stuck\", talk about the space-time characteristics of your solution. If you run out of time in your interview, it's okay! That's why being vocal matters."
  };

  const handleResponseChange = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShowHint(false);
      setShowExplanation(false);
      setShowFeedback(false);
      setAiFeedback('');
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setShowHint(false);
      setShowExplanation(false);
      setShowFeedback(false);
      setAiFeedback('');
    }
  };

  const resetPractice = () => {
    setSelectedProblem(null);
    setCurrentStep(0);
    setResponses({ R: '', E: '', A: '', C: '', T: '', O: '' });
    setShowHint(false);
    setShowExplanation(false);
    setShowFeedback(false);
    setAiFeedback('');
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return '#22c55e';
      case 'Medium': return '#f59e0b';
      case 'Hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Clone code from step C to current step (T or O)
  const cloneFromCode = () => {
    if (responses['C'] && (step.key === 'T' || step.key === 'O')) {
      const prefix = step.key === 'T' 
        ? '// Code copied from Step C - Now trace through with test cases:\n\n'
        : '// Code copied from Step C - Now analyze and optimize:\n\n';
      handleResponseChange(step.key, prefix + responses['C']);
    }
  };

  // Get AI feedback for the current step
  const getAiFeedback = async () => {
    if (!responses[step.key] || responses[step.key].trim().length < 10) {
      setAiFeedback("Please write more content before requesting feedback (at least a few sentences).");
      setShowFeedback(true);
      return;
    }

    setIsLoadingFeedback(true);
    setShowFeedback(true);
    setAiFeedback('');

    const stepContext = {
      R: `The user is practicing the REPEAT step of REACTO for the problem: "${selectedProblem.title}". They should restate the problem in their own words and ask clarifying questions. Problem description: ${selectedProblem.description}`,
      E: `The user is practicing the EXAMPLES step of REACTO for the problem: "${selectedProblem.title}". They should create example inputs/outputs and consider edge cases. Problem description: ${selectedProblem.description}`,
      A: `The user is practicing the APPROACH step of REACTO for the problem: "${selectedProblem.title}". They should plan their approach and discuss time/space complexity before coding. Problem description: ${selectedProblem.description}`,
      O: `The user is practicing the OPTIMIZE step of REACTO for the problem: "${selectedProblem.title}". They should analyze Big-O complexity and suggest optimizations. Problem description: ${selectedProblem.description}. Their code from step C was: ${responses['C'] || 'Not provided'}`
    };

    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [
            { 
              role: "user", 
              content: `You are a supportive coding interview coach helping a student practice the REACTO framework. ${stepContext[step.key]}

Here is what the student wrote:
"""
${responses[step.key]}
"""

Provide brief, encouraging feedback (2-3 paragraphs max) that:
1. Acknowledges what they did well
2. Suggests 1-2 specific improvements
3. Gives a tip for how this would help in a real interview

Keep the tone friendly and constructive. Use simple formatting, no markdown headers.`
            }
          ],
        })
      });

      const data = await response.json();
      const feedbackText = data.content
        .map(item => (item.type === "text" ? item.text : ""))
        .filter(Boolean)
        .join("\n");
      
      setAiFeedback(feedbackText || "Unable to generate feedback. Please try again.");
    } catch (error) {
      console.error("AI Feedback error:", error);
      setAiFeedback("Sorry, there was an error getting feedback. Please check your connection and try again.");
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  // Problem Selection Screen
  if (!selectedProblem) {
    return (
      <div style={{
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: '40px 20px'
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
          * { box-sizing: border-box; }
          textarea:focus, button:focus { outline: none; }
          ::selection { background: ${theme.primary}; color: white; }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes slide-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Theme Toggle */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              style={{
                background: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: '12px',
                padding: '10px 16px',
                color: theme.text,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              {isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </button>
          </div>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              animation: 'float 3s ease-in-out infinite'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLighter})`,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: '700',
                color: 'white',
                boxShadow: `0 8px 32px ${theme.primaryGlow}`
              }}>R</div>
              <span style={{
                fontSize: '42px',
                fontWeight: '700',
                color: theme.text
              }}>REACTO</span>
            </div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '600',
              color: theme.textMuted,
              margin: '0 0 16px 0',
              letterSpacing: '-0.5px'
            }}>Coding Interview Practice</h1>
            <p style={{
              fontSize: '16px',
              color: theme.textDim,
              maxWidth: '500px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Using structured problem solving to help build clarity in coding interviews
            </p>
          </div>

          {/* Framework Overview */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '8px',
            marginBottom: '48px',
            padding: '24px',
            background: theme.cardBg,
            borderRadius: '16px',
            border: `1px solid ${theme.cardBorder}`
          }}>
            {steps.map((step, i) => (
              <div 
                key={step.key} 
                style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  borderRadius: '12px',
                  background: step.type === 'code' 
                    ? theme.successBg
                    : theme.pinkBg,
                  border: `1px solid ${step.type === 'code' ? theme.success + '30' : theme.pink + '30'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: hoveredStep === step.key ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: hoveredStep === step.key 
                    ? `0 0 20px ${step.type === 'code' ? theme.success + '60' : theme.pink + '60'}, 0 0 40px ${step.type === 'code' ? theme.success + '30' : theme.pink + '30'}`
                    : 'none',
                  position: 'relative'
                }}
                onMouseEnter={() => setHoveredStep(step.key)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: step.type === 'code' ? theme.success : theme.pink,
                  marginBottom: '4px'
                }}>{step.key}</div>
                <div style={{
                  fontSize: '11px',
                  color: theme.textDim,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>{step.name}</div>
                
                {/* Tooltip */}
                {hoveredStep === step.key && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '8px',
                    padding: '10px 14px',
                    background: isDarkMode ? '#1a3a4a' : '#ffffff',
                    border: `1px solid ${theme.cardBorder}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    animation: 'fade-in 0.2s ease'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      color: theme.text,
                      fontWeight: '500'
                    }}>{stepTooltips[step.key]}</div>
                    {/* Tooltip arrow */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: `6px solid ${isDarkMode ? '#1a3a4a' : '#ffffff'}`
                    }}></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Problem Cards */}
          <h2 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: theme.textDim,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '20px'
          }}>Select a Problem</h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {problems.map((problem, index) => (
              <button
                key={problem.id}
                onClick={() => setSelectedProblem(problem)}
                style={{
                  background: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: '16px',
                  padding: '24px 28px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  animation: `slide-up 0.5s ease ${index * 0.1}s both`
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = theme.cardHover;
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.transform = 'translateX(8px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = theme.cardBg;
                  e.currentTarget.style.borderColor = theme.cardBorder;
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: theme.text
                  }}>{problem.title}</span>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: getDifficultyColor(problem.difficulty),
                    background: `${getDifficultyColor(problem.difficulty)}15`,
                    border: `1px solid ${getDifficultyColor(problem.difficulty)}30`
                  }}>{problem.difficulty}</span>
                </div>
                <p style={{
                  margin: 0,
                  color: theme.textDim,
                  fontSize: '14px',
                  lineHeight: '1.6',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>{problem.description}</p>
              </button>
            ))}
          </div>

          {/* More about REACTO Section */}
          <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: `1px solid ${theme.cardBorder}`, textAlign: 'center' }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: theme.textDim,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '16px'
            }}>More about REACTO</h2>
            
            <p style={{
              fontSize: '15px',
              color: theme.textMuted,
              marginBottom: '24px',
              lineHeight: '1.6',
              maxWidth: '500px',
              margin: '0 auto 24px auto'
            }}>
              This interview framework was invented by{' '}
              <a 
                href="https://www.linkedin.com/in/nimit/" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: theme.primary, textDecoration: 'none', fontWeight: '500' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >Nimit Maru</a>
              {' '}and{' '}
              <a 
                href="https://www.linkedin.com/in/davidgyang/" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: theme.primary, textDecoration: 'none', fontWeight: '500' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >David Yang</a>!
            </p>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '16px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {[
                {
                  title: "REACTO: Technical Interview Prep the Fullstack Way",
                  source: "Medium - Sarah's Code",
                  url: "https://medium.com/@sarahscode/reacto-technical-interview-prep-the-fullstack-way-706929a44e90"
                },
                {
                  title: "The REACTO Pattern for Acing Technical Interviews",
                  source: "Grace Hopper Program",
                  url: "https://www.gracehopper.com/blog/the-reacto-pattern-for-acing-technical-interviews"
                },
                {
                  title: "How To Answer Algorithm Interview Questions: The REACTO Method",
                  source: "Medium - Sounds Klever",
                  url: "https://medium.com/sounds-klever/how-to-answer-algorithm-interview-questions-the-reacto-method-389a0be47d2f"
                },
                {
                  title: "Whiteboard Coding Interviews: A 6 Step Process to Solve Any Problem",
                  source: "Fullstack Academy",
                  url: "https://www.fullstackacademy.com/blog/whiteboard-coding-interviews-a-6-step-process-to-solve-any-problem"
                },
                {
                  title: "A Technical Interview Cheat Sheet: REACTO",
                  source: "Medium - Enosie",
                  url: "https://enosie.medium.com/a-technical-interview-cheat-sheet-reacto-38fd09ecbcfc"
                }
              ].map((article, index) => (
                <a
                  key={index}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: theme.cardBg,
                    border: `1px solid ${theme.cardBorder}`,
                    borderRadius: '12px',
                    padding: '20px',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    display: 'block',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.cardHover;
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = theme.cardBg;
                    e.currentTarget.style.borderColor = theme.cardBorder;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: theme.text,
                    marginBottom: '8px',
                    lineHeight: '1.4'
                  }}>{article.title}</div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.textDim
                  }}>{article.source} ↗</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Practice Screen
  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        textarea:focus, button:focus { outline: none; }
        ::selection { background: ${theme.primary}; color: white; }
        textarea::placeholder { color: ${theme.textDim}; }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Top Navigation Bar */}
      <div style={{
        padding: '16px 24px',
        borderBottom: `1px solid ${theme.cardBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: theme.navBg,
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={resetPractice}
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: '10px',
              padding: '10px 16px',
              color: theme.textMuted,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            ← Problems
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: theme.text }}>{selectedProblem.title}</h1>
            <span style={{
              fontSize: '12px',
              color: getDifficultyColor(selectedProblem.difficulty),
              fontWeight: '500'
            }}>{selectedProblem.difficulty}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Progress Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {steps.map((s, i) => (
              <div
                key={s.key}
                onClick={() => { setCurrentStep(i); setShowHint(false); setShowExplanation(false); setShowFeedback(false); setAiFeedback(''); }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: i === currentStep 
                    ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLighter})` 
                    : responses[s.key] 
                      ? theme.successBg 
                      : theme.cardBg,
                  color: i === currentStep ? 'white' : responses[s.key] ? theme.success : theme.textDim,
                  border: i === currentStep 
                    ? 'none' 
                    : responses[s.key] 
                      ? `1px solid ${theme.success}50` 
                      : `1px solid ${theme.cardBorder}`,
                  boxShadow: i === currentStep ? `0 4px 20px ${theme.primaryGlow}` : 'none'
                }}
              >
                {s.key}
              </div>
            ))}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: '10px',
              padding: '10px 14px',
              color: theme.text,
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.3s ease'
            }}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0',
        height: 'calc(100vh - 69px)'
      }}>
        {/* Left Panel - Problem & Context */}
        <div style={{
          borderRight: `1px solid ${theme.cardBorder}`,
          padding: '32px',
          overflowY: 'auto',
          background: theme.panelBg
        }}>
          {/* Problem Description */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textDim,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>Problem</h3>
            <p style={{
              margin: 0,
              color: theme.text,
              fontSize: '15px',
              lineHeight: '1.7'
            }}>{selectedProblem.description}</p>
          </div>

          {/* Examples */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textDim,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>Examples</h3>
            {selectedProblem.examples.map((ex, i) => (
              <div key={i} style={{
                background: theme.codeBg,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                border: `1px solid ${theme.cardBorder}`,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '13px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: theme.textDim }}>Input: </span>
                  <span style={{ color: theme.primary }}>{ex.input}</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: theme.textDim }}>Output: </span>
                  <span style={{ color: theme.success }}>{ex.output}</span>
                </div>
                {ex.explanation && (
                  <div style={{ color: theme.textDim, fontSize: '12px', fontStyle: 'italic' }}>
                    {ex.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Current Step Info */}
          <div style={{
            background: step.type === 'code' 
              ? theme.successBg
              : theme.explanationBg,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${step.type === 'code' ? theme.success + '30' : theme.primary + '30'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: step.type === 'code' 
                  ? `linear-gradient(135deg, ${theme.success}, ${theme.success}dd)` 
                  : `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLighter})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700',
                color: 'white'
              }}>{step.key}</div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: theme.text }}>{step.name}</h2>
                <span style={{ fontSize: '13px', color: theme.textDim }}>Step {currentStep + 1} of {steps.length}</span>
              </div>
            </div>
            <p style={{
              margin: 0,
              color: theme.textMuted,
              fontSize: '14px',
              lineHeight: '1.6'
            }}>{step.description}</p>
          </div>

          {/* Why is this important? Button - shown for ALL steps */}
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={() => setShowExplanation(!showExplanation)}
              style={{
                background: theme.explanationBg,
                border: `1px solid ${theme.primary}30`,
                borderRadius: '10px',
                padding: '12px 20px',
                color: theme.primary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                width: 'fit-content'
              }}
            >
              🎯 {showExplanation ? 'Hide Explanation' : 'Why is this important?'}
            </button>
            {showExplanation && (
              <div style={{
                padding: '16px',
                background: theme.explanationBg,
                borderRadius: '12px',
                border: `1px solid ${theme.primary}25`,
                animation: 'fade-in 0.3s ease'
              }}>
                <p style={{ margin: 0, color: theme.text, fontSize: '14px', lineHeight: '1.7' }}>
                  {stepExplanations[step.key]}
                </p>
              </div>
            )}

          {/* Hint Button - only for R, E, A steps */}
          {(step.key === 'R' || step.key === 'E' || step.key === 'A') && (
            <>
              <button
                onClick={() => setShowHint(!showHint)}
                style={{
                  background: theme.warningBg,
                  border: `1px solid ${theme.warning}30`,
                  borderRadius: '10px',
                  padding: '12px 20px',
                  color: theme.warning,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  width: 'fit-content'
                }}
              >
                💡 {showHint ? 'Hide Hint' : 'Need a Hint?'}
              </button>
              {showHint && (
                <div style={{
                  padding: '16px',
                  background: theme.warningBg,
                  borderRadius: '12px',
                  border: `1px solid ${theme.warning}25`,
                  animation: 'fade-in 0.3s ease'
                }}>
                  <p style={{ margin: 0, color: theme.text, fontSize: '14px', lineHeight: '1.6' }}>
                    {selectedProblem.hints[step.key.toLowerCase()]}
                  </p>
                </div>
              )}
            </>
          )}

          {/* AI Feedback Button - for R, E, A, and O steps */}
          {(step.key === 'R' || step.key === 'E' || step.key === 'A' || step.key === 'O') && (
            <>
              <button
                onClick={getAiFeedback}
                disabled={isLoadingFeedback}
                style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(99, 102, 241, 0.15))',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  color: isDarkMode ? '#a78bfa' : '#7c3aed',
                  cursor: isLoadingFeedback ? 'wait' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  width: 'fit-content',
                  opacity: isLoadingFeedback ? 0.7 : 1
                }}
              >
                {isLoadingFeedback ? (
                  <>⏳ Getting Feedback...</>
                ) : (
                  <>🤖 Get AI Feedback</>
                )}
              </button>
              {showFeedback && (
                <div style={{
                  padding: '16px',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(99, 102, 241, 0.08))',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  animation: 'fade-in 0.3s ease',
                  position: 'relative'
                }}>
                  <button
                    onClick={() => setShowFeedback(false)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'transparent',
                      border: 'none',
                      color: theme.textDim,
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px'
                    }}
                  >✕</button>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '12px',
                    color: isDarkMode ? '#a78bfa' : '#7c3aed',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    🤖 AI Coach Feedback
                  </div>
                  {isLoadingFeedback ? (
                    <div style={{ color: theme.textMuted, fontSize: '14px' }}>
                      Analyzing your response...
                    </div>
                  ) : (
                    <p style={{ 
                      margin: 0, 
                      color: theme.text, 
                      fontSize: '14px', 
                      lineHeight: '1.7',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {aiFeedback}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* Right Panel - Response Area */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          background: theme.panelBg
        }}>
          {/* Editor Header */}
          <div style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${theme.cardBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: step.type === 'code' ? theme.success : theme.primary
              }}></div>
              <span style={{ color: theme.textMuted, fontSize: '14px', fontWeight: '500' }}>
                {step.type === 'code' ? 'Code Editor' : 'Your Response'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Clone from Code button - only for T and O steps */}
              {(step.key === 'T' || step.key === 'O') && responses['C'] && (
                <button
                  onClick={cloneFromCode}
                  style={{
                    padding: '6px 12px',
                    background: theme.explanationBg,
                    border: `1px solid ${theme.primary}40`,
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: theme.primary,
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.primary + '20';
                    e.currentTarget.style.borderColor = theme.primary;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = theme.explanationBg;
                    e.currentTarget.style.borderColor = theme.primary + '40';
                  }}
                >
                  📋 Clone from Code
                </button>
              )}
              {responses[step.key] && (
                <span style={{
                  padding: '4px 10px',
                  background: theme.successBg,
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: theme.success,
                  fontWeight: '500'
                }}>✓ Saved</span>
              )}
            </div>
          </div>

          {/* Text/Code Area */}
          <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column' }}>
            <textarea
              value={responses[step.key]}
              onChange={(e) => handleResponseChange(step.key, e.target.value)}
              placeholder={
                step.type === 'code' 
                  ? step.key === 'C' 
                    ? '// Write your solution here...\n\nfunction solution() {\n  \n}'
                    : step.key === 'T'
                    ? '// Walk through your code with test cases...\n// Trace the execution step by step\n\n// Test 1:\n// Input: ...\n// Step 1: ...\n// Step 2: ...\n// Output: ...'
                    : '// Analyze your solution...\n// Time Complexity: O(?)\n// Space Complexity: O(?)\n\n// Can you optimize further?\n// Alternative approach:'
                  : step.key === 'R'
                    ? 'Restate the problem in your own words...\n\nWhat are the inputs?\nWhat is the expected output?\nWhat are the constraints?\nWhat questions would you ask the interviewer?'
                    : step.key === 'E'
                    ? 'Write out example inputs and outputs...\n\nExample 1:\nInput: \nOutput: \n\nEdge cases to consider:\n- Empty input\n- Single element\n- ...'
                    : 'Describe your approach before coding...\n\nApproach 1 (Brute Force):\n- Steps: \n- Time: O(?)\n- Space: O(?)\n\nApproach 2 (Optimized):\n- Steps: \n- Time: O(?)\n- Space: O(?)'
              }
              style={{
                flex: 1,
                width: '100%',
                background: theme.inputBg,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: '12px',
                padding: '20px',
                color: theme.text,
                fontSize: step.type === 'code' ? '14px' : '15px',
                fontFamily: step.type === 'code' ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
                lineHeight: '1.7',
                resize: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={e => e.target.style.borderColor = step.type === 'code' ? theme.success : theme.primary}
              onBlur={e => e.target.style.borderColor = theme.cardBorder}
            />
          </div>

          {/* Navigation Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${theme.cardBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={goToPrevStep}
              disabled={currentStep === 0}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: `1px solid ${theme.cardBorder}`,
                background: currentStep === 0 ? 'transparent' : theme.cardBg,
                color: currentStep === 0 ? theme.textDim : theme.textMuted,
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              ← Previous
            </button>

            <div style={{ 
              flex: 1, 
              height: '4px', 
              background: theme.cardBg, 
              borderRadius: '2px',
              margin: '0 24px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${theme.primary}, ${theme.success})`,
                borderRadius: '2px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>

            {currentStep < steps.length - 1 ? (
              <button
                onClick={goToNextStep}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLighter})`,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: `0 4px 20px ${theme.primaryGlow}`,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Next Step →
              </button>
            ) : (
              <button
                onClick={resetPractice}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${theme.success}, ${theme.success}dd)`,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: `0 4px 20px ${theme.success}40`,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                ✓ Complete Practice
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
